import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, User, Archive, Newspaper, MapPin, MessageCircle, Loader2, X } from 'lucide-react';
import { globalSearch } from '../services/api';

const SECTION_META = {
  people:        { label: 'People',        icon: <User className="w-4 h-4" />,          color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  archive_items: { label: 'Archive Items', icon: <Archive className="w-4 h-4" />,        color: 'text-amber-700 bg-amber-50 border-amber-200' },
  news:          { label: 'News',           icon: <Newspaper className="w-4 h-4" />,     color: 'text-blue-700 bg-blue-50 border-blue-200' },
  locations:     { label: 'Locations',     icon: <MapPin className="w-4 h-4" />,         color: 'text-purple-700 bg-purple-50 border-purple-200' },
  chat_messages: { label: 'Chat',          icon: <MessageCircle className="w-4 h-4" />,  color: 'text-gray-700 bg-gray-50 border-gray-200' },
};

function ResultCard({ item, type }) {
  switch (type) {
    case 'people':
      return (
        <Link
          to={`/archive/${encodeURIComponent(item.category || '')}/${encodeURIComponent(item.subcategory || '')}/person/${item.id}`}
          className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-emerald-400 hover:shadow-md transition group"
        >
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {item.photo
              ? <img src={item.photo} alt={item.full_name} className="w-full h-full object-cover" />
              : <User className="w-6 h-6 text-emerald-600" />}
          </div>
          <div>
            <p className="font-semibold text-gray-900 group-hover:text-emerald-700">{item.full_name}</p>
            {item.profession && item.profession !== '-' && <p className="text-sm text-gray-500">{item.profession}</p>}
            <p className="text-xs text-emerald-600 mt-0.5">{item.category} › {item.subcategory}</p>
          </div>
        </Link>
      );
    case 'archive_items':
      return (
        <div className="p-4 bg-white rounded-xl border border-gray-200 hover:border-amber-400 hover:shadow-md transition">
          <p className="font-semibold text-gray-900">{item.title}</p>
          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.description}</p>
          <p className="text-xs text-amber-600 mt-1">{item.category} › {item.subcategory} • {item.date}</p>
        </div>
      );
    case 'news':
      return (
        <Link to={`/news/${item.id}`} className="block p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-400 hover:shadow-md transition group">
          <p className="font-semibold text-gray-900 group-hover:text-blue-700">{item.title}</p>
          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.content}</p>
          <p className="text-xs text-blue-500 mt-1">{item.author} • {item.date}</p>
        </Link>
      );
    case 'locations':
      return (
        <div className="p-4 bg-white rounded-xl border border-gray-200 hover:border-purple-400 hover:shadow-md transition">
          <p className="font-semibold text-gray-900">{item.name}</p>
          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.description}</p>
          {item.address && item.address !== '-' && <p className="text-xs text-purple-600 mt-1">📍 {item.address}</p>}
        </div>
      );
    case 'chat_messages':
      return (
        <div className="p-4 bg-white rounded-xl border border-gray-200">
          <p className="text-sm font-medium text-gray-700">
            <span className="text-emerald-700">@{item.username}</span> in chatroom
          </p>
          <p className="text-sm text-gray-600 mt-1">{item.content}</p>
          <p className="text-xs text-gray-400 mt-1">{new Date(item.created_at).toLocaleDateString()}</p>
        </div>
      );
    default:
      return null;
  }
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const [inputVal, setInputVal] = useState(initialQ);
  const [results, setResults]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const doSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) return;
    setLoading(true);
    setError('');
    try {
      const res = await globalSearch(q.trim());
      setResults(res.data);
    } catch (_e) {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialQ) doSearch(initialQ);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputVal.trim().length < 2) return;
    setSearchParams({ q: inputVal });
    setActiveTab('all');
    doSearch(inputVal);
  };

  const clearSearch = () => {
    setInputVal('');
    setResults(null);
    setSearchParams({});
  };

  const allItems = results
    ? Object.entries(results.results).flatMap(([type, items]) => items.map(i => ({ ...i, _type: type })))
    : [];

  const tabs = [
    { key: 'all', label: `All (${allItems.length})` },
    ...Object.entries(SECTION_META).map(([key, meta]) => ({
      key,
      label: `${meta.label} (${results?.results?.[key]?.length || 0})`,
      icon: meta.icon,
    })),
  ];

  const displayedItems = activeTab === 'all'
    ? allItems
    : (results?.results?.[activeTab] || []).map(i => ({ ...i, _type: activeTab }));

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Search Gbongan Archive</h1>
        <p className="text-gray-500">Search people, places, news and chat across the entire archive</p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSubmit} className="flex gap-2 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            placeholder="Search names, places, topics…"
            className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 text-base"
          />
          {inputVal && (
            <button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || inputVal.trim().length < 2}
          className="px-6 py-3 bg-emerald-700 text-white rounded-xl font-semibold hover:bg-emerald-800 transition disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Search
        </button>
      </form>

      {error && <p className="text-red-600 text-center mb-6">{error}</p>}

      {results && (
        <>
          <p className="text-sm text-gray-500 mb-4">
            Found <strong>{results.total_results}</strong> results for "<strong>{results.query}</strong>"
          </p>

          {/* Tabs */}
          <div className="flex gap-2 flex-wrap mb-6">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition border ${
                  activeTab === tab.key
                    ? 'bg-emerald-700 text-white border-emerald-700'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Results */}
          {displayedItems.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No results in this category.</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {displayedItems.map((item, i) => (
                <ResultCard key={`${item._type}-${item.id}-${i}`} item={item} type={item._type} />
              ))}
            </div>
          )}
        </>
      )}

      {!results && !loading && (
        <div className="text-center py-20 text-gray-300">
          <Search className="w-16 h-16 mx-auto mb-4" />
          <p className="text-gray-400">Start typing to search the Gbongan Archive</p>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {['Professors', 'Doctors', 'Rivers', 'History', 'Politicians', 'Nollywood'].map(s => (
              <button
                key={s}
                className="text-sm px-3 py-1 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
